import { useState, type ReactNode } from 'react';
import { Badge, Button, cn, ErrorState, Skeleton } from '@/core/ui';
import { ArrowLeft, ArrowSquareOut, Check, EnvelopeSimple } from '@/core/ui/icons';
import { useCompleteVisit, useMarkContacting, useVisit } from '../application/queries';
import {
  canCancel,
  canComplete,
  canMarkContacting,
  canSchedule,
  formatDate,
  formatDay,
  formatVisitWhen,
  hasSlot,
  modalityLabel,
  PERIODS,
  placeText,
  STATUS_LABELS,
  STATUS_TONES,
  VisitStatus,
  WEEKDAYS,
  type Visit,
} from '../domain/visit';
import { actionError } from './errors';
import { CancelDialog, ScheduleDialog } from './VisitDialogs';

/** O pedido inteiro: contato, quando a pessoa pode, o que ela escreveu e os passos do time. */
export function VisitDetail({ visitId, onBack }: { visitId: string; onBack: () => void }) {
  const query = useVisit(visitId);

  return (
    <div className="rounded-2xl border border-line bg-surface">
      <div className="border-b border-line px-5 py-3 lg:hidden">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-fg-subtle hover:text-fg"
        >
          <ArrowLeft aria-hidden className="size-4" />
          Voltar à lista
        </button>
      </div>
      {query.isPending ? (
        <div className="space-y-3 p-5">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-40" />
        </div>
      ) : query.isError ? (
        <div className="p-5">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      ) : (
        <Content visit={query.data} />
      )}
    </div>
  );
}

function Content({ visit }: { visit: Visit }) {
  const [dialog, setDialog] = useState<'schedule' | 'cancel' | null>(null);
  const contacting = useMarkContacting();
  const complete = useCompleteVisit();
  const error = contacting.error ?? complete.error;
  const close = () => setDialog(null);

  return (
    <article aria-labelledby="visit-title" className="divide-y divide-line">
      <header className="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={STATUS_TONES[visit.status] ?? 'neutral'}>
            {STATUS_LABELS[visit.status] ?? visit.status}
          </Badge>
          <span className="text-xs text-fg-subtle">Pedido em {formatDate(visit.requested_at)}</span>
        </div>
        <h2 id="visit-title" className="mt-2 text-lg font-semibold text-fg">
          {visit.requester_name || 'Sem nome no perfil'}
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <a
            href={visit.whatsapp_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand-400 px-3 text-sm font-medium text-on-brand hover:bg-brand-500"
          >
            WhatsApp {visit.phone_display}
            <ArrowSquareOut aria-hidden className="size-3.5" />
          </a>
          {visit.requester_email && (
            <a
              href={`mailto:${visit.requester_email}`}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-line-strong px-3 text-sm text-fg-subtle hover:bg-surface-2 hover:text-fg"
            >
              <EnvelopeSimple aria-hidden className="size-4" />
              {visit.requester_email}
            </a>
          )}
        </div>
      </header>

      {(visit.status === VisitStatus.SCHEDULED || visit.status === VisitStatus.COMPLETED) &&
        visit.scheduled_for && (
          <Section title={visit.status === VisitStatus.COMPLETED ? 'Visita realizada' : 'Visita agendada'}>
            <dl className="grid gap-2 text-sm">
              <Row label="Quando">{formatVisitWhen(visit.scheduled_for)}</Row>
              <Row label="Analista">{visit.analyst_name}</Row>
              <Row label="Modalidade">{modalityLabel(visit.modality)}</Row>
              {visit.location && (
                <Row label={visit.modality === 'online' ? 'Link' : 'Local'}>
                  {/^https?:\/\//i.test(visit.location) ? (
                    <a
                      href={visit.location}
                      target="_blank"
                      rel="noreferrer"
                      className="break-all text-brand-700 hover:underline"
                    >
                      {visit.location}
                    </a>
                  ) : (
                    visit.location
                  )}
                </Row>
              )}
              {visit.team_note && <Row label="Nota do time">{visit.team_note}</Row>}
            </dl>
          </Section>
        )}

      {visit.status === VisitStatus.CANCELLED && (
        <Section title={visit.cancelled_by === 'team' ? 'Cancelado pelo time' : 'Cancelado pela pessoa'}>
          <p className="text-sm whitespace-pre-line text-fg-muted">
            {visit.cancel_reason || 'Sem motivo informado.'}
          </p>
        </Section>
      )}

      <Section title="Quando pode receber">
        <AvailabilityGrid visit={visit} />
        {visit.available_from && (
          <p className="mt-2 text-sm text-fg-muted">A partir de {formatDay(visit.available_from)}</p>
        )}
      </Section>

      <Section title="Sobre o pedido">
        <dl className="grid gap-2 text-sm">
          <Row label="Cidade">{placeText(visit)}</Row>
          {visit.specialty && <Row label="Especialidade">{visit.specialty}</Row>}
          {visit.topic && <Row label="Assunto">{visit.topic}</Row>}
        </dl>
        {visit.notes && (
          <blockquote className="mt-3 rounded-lg bg-surface-2 p-3 text-sm whitespace-pre-line text-fg-muted">
            {visit.notes}
          </blockquote>
        )}
      </Section>

      <Section title="Histórico">
        <ol className="space-y-1 text-sm text-fg-muted">
          <Step at={visit.requested_at}>Pedido feito no portal</Step>
          <Step at={visit.contacted_at}>Time entrou em contato</Step>
          <Step at={visit.scheduled_at}>Visita agendada (última alteração)</Step>
          <Step at={visit.completed_at}>Visita concluída</Step>
          <Step at={visit.cancelled_at}>Pedido cancelado</Step>
        </ol>
      </Section>

      {(canMarkContacting(visit) || canSchedule(visit) || canComplete(visit) || canCancel(visit)) && (
        <footer className="flex flex-wrap gap-2 p-5">
          {canMarkContacting(visit) && (
            <Button
              variant="secondary"
              loading={contacting.isPending}
              onClick={() => contacting.mutate(visit.id)}
            >
              Marcar em contato
            </Button>
          )}
          {canSchedule(visit) && (
            <Button
              variant={visit.status === VisitStatus.SCHEDULED ? 'secondary' : 'primary'}
              onClick={() => setDialog('schedule')}
            >
              {visit.status === VisitStatus.SCHEDULED ? 'Reagendar' : 'Agendar'}
            </Button>
          )}
          {canComplete(visit) && (
            <Button loading={complete.isPending} onClick={() => complete.mutate(visit.id)}>
              <Check aria-hidden className="size-4" />
              Concluir visita
            </Button>
          )}
          {canCancel(visit) && (
            <Button variant="ghost" onClick={() => setDialog('cancel')}>
              Cancelar pedido
            </Button>
          )}
          {error && (
            <p role="alert" className="w-full text-sm text-fg">
              {actionError(error)}
            </p>
          )}
        </footer>
      )}

      <ScheduleDialog visit={visit} open={dialog === 'schedule'} onClose={close} />
      <CancelDialog visit={visit} open={dialog === 'cancel'} onClose={close} />
    </article>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="p-5">
      <h3 className="mb-3 text-xs font-semibold tracking-wide text-fg-subtle uppercase">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-2">
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="whitespace-pre-line text-fg">{children}</dd>
    </div>
  );
}

function Step({ at, children }: { at: string | null | undefined; children: ReactNode }) {
  if (!at) return null;
  return (
    <li className="flex justify-between gap-3">
      <span>{children}</span>
      <time dateTime={at} className="shrink-0 text-fg-subtle tabular-nums">
        {formatVisitWhen(at)}
      </time>
    </li>
  );
}

/** A grade que a pessoa marcou no portal: dias × períodos. */
function AvailabilityGrid({ visit }: { visit: Visit }) {
  return (
    <table className="w-full table-fixed text-center text-xs">
      <thead>
        <tr>
          <th scope="col" className="w-12">
            <span className="sr-only">Dia</span>
          </th>
          {PERIODS.map((p) => (
            <th key={p.value} scope="col" className="pb-1 font-medium text-fg-subtle">
              {p.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {WEEKDAYS.map((d) => (
          <tr key={d.value}>
            <th scope="row" className="py-0.5 text-left font-medium text-fg-subtle">
              {d.short}
            </th>
            {PERIODS.map((p) => {
              const on = hasSlot(visit, d.value, p.value);
              return (
                <td key={p.value} className="p-0.5">
                  <span
                    className={cn(
                      'flex h-7 items-center justify-center rounded-md',
                      on ? 'bg-brand-400 text-on-brand' : 'bg-surface-2 text-gray-300',
                    )}
                  >
                    {on ? <Check aria-hidden className="size-3.5" /> : <span aria-hidden>·</span>}
                    <span className="sr-only">{on ? 'pode' : 'não pode'}</span>
                  </span>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
